import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { forkJoin, from, Subject } from "rxjs";
import { filter, finalize, switchMap, take, takeUntil } from "rxjs/operators";

import { ScoreLocalService } from "@app/pages/score/score.service";
import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { SCORE_SESSION_ROUTE } from "./h01-home-introduction.service";
import { isOnRoute, OnboardingModalExit, openOnboardingCentredModal, whenUiQuiet } from "./onboarding-lesson-modal";

export const S01_LESSON_ID = "S01";

/** Where S01 shows - the Score landing page, not the scoring session page (workbook v2.37). */
export const SCORE_LANDING_ROUTE = "/score";

/** Recorded with S01's completed lesson state (spec §15.6) - see the class comment. */
export const SCORE_INTRODUCTION_VIEWED = "score_introduction_viewed";

/** Copy rows S01.01-S01.04, workbook v2.37 Copy sheet (all Reviewed: Confirmed). */
export const S01_COPY = {
  heading: "Check in with your Safety Buffer",
  body:
    "Score brings together the things supporting you and the demands your system is carrying " +
    "right now. Add at least one Star, add any Shadows that feel relevant, then complete your " +
    "Score to see your current Safety Buffer.",
  primaryLabel: "Start scoring",
  secondaryLabel: "I'll explore myself",
};

/**
 * Onboarding lesson S01 "Check in with your Safety Buffer" (FLIP-339; spec v2.35 §15.1, workbook
 * v2.37). Informational centred modal over the Score landing page (/score).
 *
 * - Eligible on the user's first Score landing visit while the `onboarding-walkthrough` flag is on,
 *   S01 has not been completed or dismissed, and the user has never submitted a Score - which is
 *   also the "never reactivated once the first Score is complete" and existing-user (§26.7) rules.
 * - Suppressed when H01 or H02 started the active scoring session (their Start Score marks
 *   ScoreLocalService's onboarding direct entry, held until reload). Suppression records nothing.
 * - Opens only once no blocking product modal/snackbar is open or about to be, and the user is still
 *   on the Score landing page. At most once per app session.
 * - Every exit completes S01 permanently: Start scoring, I'll explore myself, or the standard
 *   informational dismissal (close X / Escape / Android Back). Backdrop taps do nothing. Start
 *   scoring then opens the scoring session (the modal sits on the landing page, so closing alone
 *   would leave the user short of it); the other exits stay on the landing page.
 * - The milestone `score_introduction_viewed` is written with S01's completed state, so S01's
 *   CompletedAt on its UserOnboardingStep row *is* that milestone (same convention as H01).
 */
@Injectable({ providedIn: "root" })
export class S01ScoreIntroductionService {
  /** Emits when S01 completes, so S02 (FLIP-340) can follow it if it was still open. */
  readonly completed$ = new Subject<void>();

  private readonly cancel$ = new Subject<void>();
  private inFlight = false;
  /** Set once S01 has opened in this app session, so a re-visit doesn't re-check it. */
  private shown = false;
  /** True from the modal opening until its exit - read by S02 (isOpen). */
  private open = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private blockingUiSignal: BlockingUiSignalService,
    private featureFlagService: FeatureFlagService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private scoreLocalService: ScoreLocalService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this session's state so the next login gets its own check.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.shown = false;
      this.cancel();
    });
  }

  /** Whether the S01 modal is showing right now. S02 waits for it to close before it follows. */
  get isOpen(): boolean {
    return this.open;
  }

  /** Call on each Score landing page visit. No-op if a check is running or S01 already showed. */
  maybeShow(): void {
    if (this.inFlight || this.shown || this.scoreLocalService.isOnboardingDirectEntry()) {
      return;
    }
    this.inFlight = true;

    // Wait for a confirmed subscription - that's when app.component's authenticated boot fan-out
    // starts the flag fetch and registers the boot-modal pending checks (see H01).
    this.paymentsService.subscriptionActive$
      .pipe(
        filter((active) => active),
        take(1),
        switchMap(() => from(this.featureFlagService.init())),
        filter(() => this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)),
        switchMap(() =>
          forkJoin([
            this.onboardingStepService.getStep(S01_LESSON_ID),
            this.onboardingStepService.hasSubmittedScore(),
          ])
        ),
        // Only a definite "no row / not finished" and a definite "no Score" qualify - a failed
        // check holds back rather than guessing.
        filter(
          ([step, hasScore]) =>
            step !== undefined && !step?.completedAt && !step?.dismissed && hasScore === false
        ),
        switchMap(() => whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$)),
        // Re-checked at open time: H01/H02 may have started a session while this check waited.
        filter(
          () => isOnRoute(this.router, SCORE_LANDING_ROUTE) && !this.scoreLocalService.isOnboardingDirectEntry()
        ),
        takeUntil(this.cancel$),
        finalize(() => (this.inFlight = false))
      )
      .subscribe(() => this.present());
  }

  /** Call when leaving the Score landing page - S01 never opens over another page. */
  cancel(): void {
    this.cancel$.next();
  }

  private present(): void {
    this.shown = true;
    this.open = true;
    const now = new Date();
    this.onboardingStepService.upsertStep({ stepKey: S01_LESSON_ID, startedAt: now, lastViewedAt: now });
    this.track("onboarding_lesson_state_changed", "started");

    const exit$ = openOnboardingCentredModal(this.dialog, S01_COPY);
    this.track("onboarding_lesson_presented");
    exit$.subscribe((exit) => this.complete(exit));
  }

  /**
   * Informational: every exit - either button or the standard dismissal - completes S01. Start
   * scoring also opens the scoring session.
   */
  private complete(exit: OnboardingModalExit): void {
    this.open = false;
    this.onboardingStepService.upsertStep({ stepKey: S01_LESSON_ID, completedAt: new Date() });

    const action = exit === "primary" ? "start_scoring" : exit === "secondary" ? "explore_myself" : "dismiss";
    this.track("onboarding_lesson_action", `action: ${action}`);
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${SCORE_INTRODUCTION_VIEWED}`);
    this.completed$.next();

    if (exit === "primary") {
      this.router.navigate([SCORE_SESSION_ROUTE]);
    }
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${S01_LESSON_ID}; ${detail}` : `lesson: ${S01_LESSON_ID}`,
    });
  }
}
